/* David Pladen — header behaviour (sections/dp-header.liquid)
 * Demo behaviour: the hamburger toggles the mega menu, which closes on a
 * second click, an outside click, a link click or Escape. The burger opens
 * #mnav (focus trap, scroll lock, closes on ×, link, Escape).
 */

if (!customElements.get('dp-header')) {
  const FOCUSABLE =
    'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), summary, [tabindex]:not([tabindex="-1"])';

  customElements.define(
    'dp-header',
    class DpHeader extends HTMLElement {
      connectedCallback() {
        this.header = this.querySelector('.hdr');
        this.mega = { button: this.querySelector('.ham'), panel: this.querySelector('.mega') };
        this.country = {
          button: this.querySelector('.ctry'),
          panel: this.querySelector('.dp-country__panel'),
        };
        this.mnav = this.querySelector('.mnav');
        this.burger = this.querySelector('.burger');

        this.onDocumentClick = this.onDocumentClick.bind(this);
        this.onKeydown = this.onKeydown.bind(this);
        this.onBreakpoint = this.onBreakpoint.bind(this);
        this.desktop = window.matchMedia('(min-width: 861px)');

        this.mega.button?.addEventListener('click', () => this.toggle(this.mega));
        this.mega.panel?.addEventListener('click', (event) => {
          if (event.target.closest('a')) this.toggle(this.mega, false);
        });
        this.country.button?.addEventListener('click', () => this.toggle(this.country));

        this.burger?.addEventListener('click', () => this.openMnav());
        this.mnav?.querySelector('.x')?.addEventListener('click', () => this.closeMnav());
        this.mnav?.addEventListener('click', (event) => {
          if (event.target.closest('a')) this.closeMnav({ restoreFocus: false });
        });

        document.addEventListener('click', this.onDocumentClick);
        document.addEventListener('keydown', this.onKeydown);
        this.desktop.addEventListener('change', this.onBreakpoint);

        if (window.Shopify && Shopify.designMode) this.bindEditor();
      }

      disconnectedCallback() {
        document.removeEventListener('click', this.onDocumentClick);
        document.removeEventListener('keydown', this.onKeydown);
        this.desktop.removeEventListener('change', this.onBreakpoint);
        document.documentElement.classList.remove('dp-scroll-lock');
      }

      /* Disclosures: mega menu and country list */
      isOpen(part) {
        return part.button?.getAttribute('aria-expanded') === 'true';
      }

      toggle(part, force) {
        if (!part.button || !part.panel) return;
        const open = force === undefined ? !this.isOpen(part) : force;
        if (open) [this.mega, this.country].forEach((o) => o !== part && this.toggle(o, false));
        part.button.setAttribute('aria-expanded', String(open));
        part.panel.hidden = !open;
        if (part === this.mega) part.button.setAttribute('aria-label', (open ? part.button.dataset.closeLabel : part.button.dataset.openLabel) || '');
      }

      onDocumentClick(event) {
        if (this.isOpen(this.mega) && !this.header.contains(event.target)) this.toggle(this.mega, false);
        if (
          this.isOpen(this.country) &&
          !this.country.panel.contains(event.target) &&
          !this.country.button.contains(event.target)
        ) {
          this.toggle(this.country, false);
        }
      }

      /* Mobile menu */
      openMnav() {
        if (!this.mnav) return;
        this.mnav.hidden = false;
        this.burger.setAttribute('aria-expanded', 'true');
        document.documentElement.classList.add('dp-scroll-lock');
        this.mnav.querySelector('.x')?.focus();
      }

      closeMnav({ restoreFocus = true } = {}) {
        if (!this.mnav || this.mnav.hidden) return;
        this.mnav.hidden = true;
        this.burger.setAttribute('aria-expanded', 'false');
        document.documentElement.classList.remove('dp-scroll-lock');
        if (restoreFocus) this.burger.focus();
      }

      trapFocus(event) {
        const items = Array.from(this.mnav.querySelectorAll(FOCUSABLE)).filter((el) => el.offsetParent !== null);
        if (!items.length) return;
        const first = items[0];
        const last = items[items.length - 1];
        if (!this.mnav.contains(document.activeElement)) {
          event.preventDefault();
          first.focus();
        } else if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      }

      onKeydown(event) {
        const mnavOpen = this.mnav && !this.mnav.hidden;
        if (event.key === 'Escape') {
          if (mnavOpen) {
            this.closeMnav();
            return;
          }
          [this.mega, this.country].forEach((part) => {
            if (!this.isOpen(part)) return;
            this.toggle(part, false);
            part.button.focus();
          });
        }
        if (event.key === 'Tab' && mnavOpen) this.trapFocus(event);
      }

      onBreakpoint() {
        if (this.desktop.matches) {
          this.closeMnav({ restoreFocus: false });
        } else {
          this.toggle(this.mega, false);
          this.toggle(this.country, false);
        }
      }

      /* Theme editor: selecting a tile shows it */
      bindEditor() {
        document.addEventListener('shopify:block:select', (event) => {
          if (!this.contains(event.target)) return;
          if (this.desktop.matches) this.toggle(this.mega, true);
          else this.openMnav();
        });
        document.addEventListener('shopify:block:deselect', (event) => {
          if (!this.contains(event.target)) return;
          this.toggle(this.mega, false);
          this.closeMnav({ restoreFocus: false });
        });
      }
    }
  );
}